import { BaseGenerator } from './BaseGenerator.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Suburban home generator.
 * Produces a residential house with BSP-subdivided rooms, outdoor yard,
 * road, sidewalks, driveway, walkway, and fenced backyard.
 *
 * Options:
 * - bedrooms: 2-5 (default 3)
 * - quality: 'draft' | 'standard' | 'high'
 *
 * Ported from OMN's generateSuburbanHome.
 */
export class SuburbanHomeGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';
    const tiles = content.tiles || SuburbanHomeGenerator.DEFAULT_TILES;
    const opts = { ...SuburbanHomeGenerator.DEFAULT_OPTIONS, ...this.options };
    const bedrooms = Math.max(2, Math.min(5, opts.bedrooms || 3));

    // Size ranges per bedroom count
    const sizeTable = {
      2: { wMin: 18, wMax: 22, hMin: 12, hMax: 16, depth: 2, minRoomW: 5, minRoomH: 5 },
      3: { wMin: 22, wMax: 26, hMin: 14, hMax: 18, depth: 3, minRoomW: 5, minRoomH: 5 },
      4: { wMin: 26, wMax: 30, hMin: 16, hMax: 20, depth: 3, minRoomW: 5, minRoomH: 5 },
      5: { wMin: 28, wMax: 34, hMin: 18, hMax: 22, depth: 4, minRoomW: 5, minRoomH: 4 },
    };
    const sz = sizeTable[bedrooms];
    const houseW = this.rng.nextInt(sz.wMin, sz.wMax);
    const houseH = this.rng.nextInt(sz.hMin, sz.hMax);

    const hl = Math.floor((this.width - houseW) / 2);
    const ht = Math.floor((this.height - houseH) / 2) - 4;
    const hr = hl + houseW;
    const hb = ht + houseH;

    const roadTop = hb + 4;
    const roadHeight = 3;
    const sidewalkN = roadTop - 1;
    const sidewalkS = roadTop + roadHeight;

    // Zone grids
    const ground = this.createGrid(this.width, this.height, ZONE.GRASS);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);

    // 1. Grass with variation
    this._fillGrassZone(ground);
    this._addGrassVariation(ground, 5);

    // 2. Road + sidewalks
    this._fillRoadZone(ground, roadTop, roadHeight);
    this._fillSidewalkRow(ground, sidewalkN);
    this._fillSidewalkRow(ground, sidewalkS);

    // 3. BSP subdivision of interior
    const root = { x: hl + 1, y: ht + 1, w: houseW - 2, h: houseH - 2 };
    this._bspSplit(root, sz.depth, sz.minRoomW, sz.minRoomH);
    const leaves = this._bspLeaves(root);

    // 4. Assign room types
    const roomTypes = this._getRoomTypesForBedrooms(bedrooms);
    this._assignRoomTypes(leaves, hl, ht, houseW, houseH, roomTypes);

    // 5. Fill room floors with typed zones
    for (const leaf of leaves) {
      const zone = this._roomTypeToZone(leaf.roomType || 'living');
      this.fillRect(ground, leaf.x, leaf.y, leaf.x + leaf.w - 1, leaf.y + leaf.h - 1, zone);
    }

    // 6. Perimeter walls
    this._placePerimeterWalls(objects, hl, ht, hr, hb);

    // 7. Interior walls with doorframes
    this._placeBSPWalls(ground, objects, root);

    // 8. Exterior doors
    const frontDoorCol = Math.floor((hl + hr) / 2);
    this.setCell(objects, frontDoorCol, hb - 1, ZONE.DOOR);
    this.setCell(ground, frontDoorCol, hb - 1, ZONE.LIVING);

    const backDoorCol = hl + this.rng.nextInt(3, houseW - 4);
    this.setCell(objects, backDoorCol, ht, ZONE.DOOR);
    this.setCell(ground, backDoorCol, ht, ZONE.BEDROOM);

    // 9. Windows on exterior walls
    this._placeExteriorWindows(objects, hl, ht, hr, hb, 4);

    // 10. Walkway from front door to sidewalk (centered on door column)
    const walkwayWidth = 2;
    const walkwayCol = frontDoorCol - Math.floor(walkwayWidth / 2);
    this._placeWalkway(ground, walkwayCol, hb, sidewalkN, walkwayWidth);

    // 11. Driveway
    const drivewayCol = hr - 4;
    this._placeDriveway(ground, drivewayCol, hb, sidewalkN, 3);

    // 12. Fenced backyard
    this._placeBackyardFence(objects, hl, ht, hr, 4);

    // 13. Yard decorations
    this._scatterYardDecorations(objects, hl, ht, hr, hb, this.rng.nextInt(4, 7), this.rng.nextInt(3, 5));

    // 14. Cars
    this._placeRoadCars(objects, roadTop, hl, hr, this.rng.nextInt(2, 3));
    this.setCell(objects, drivewayCol + 1, hb + 2, ZONE.CRATE); // car placeholder

    // Build room metadata
    const rooms = leaves.map(l => ({
      x: l.x, y: l.y, width: l.w, height: l.h, type: l.roomType,
    }));

    if (quality !== 'draft') {
      const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });
      if (quality !== 'high') pipeline.removePass('decoration');

      const pipelineOptions = {
        tileMapping: this._buildTileMapping(tiles),
        floorTileIds: [ZONE.FLOOR, ZONE.DOOR, ZONE.BEDROOM, ZONE.BATHROOM,
          ZONE.KITCHEN, ZONE.LIVING, ZONE.DINING, ZONE.OFFICE, ZONE.MASTER],
        corridorTileId: ZONE.FLOOR,
        cleanup: { minPatchSize: 0, smoothIterations: 0 },
      };

      const result = pipeline.run({
        ground, objects, overhead,
        zones: ground,
        width: this.width, height: this.height,
        metadata: { rooms },
      }, pipelineOptions);

      const finalGround = this._resolveZones(result.ground, tiles);
      const finalObjects = this._resolveObjectZones(result.objects, tiles);
      const finalOverhead = result.overhead.map(row => [...row]);

      if (tiles._tileLayers) {
        this.redistributeByLayer(
          { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
          tiles._tileLayers,
        );
      }

      return this.buildTMJ({
        layers: [
          { name: 'Ground', data: this.flattenGrid(finalGround) },
          { name: 'Objects', data: this.flattenGrid(finalObjects) },
          { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
        ],
        tilesets: content.tilesets || [],
        properties: {
          generator: { type: 'string', value: 'suburban_home' },
          rooms: { type: 'string', value: JSON.stringify(rooms) },
        },
      });
    }

    // Draft mode: resolve zones directly
    const finalGround = this._resolveZones(ground, tiles);
    const finalObjects = this._resolveObjectZones(objects, tiles);
    const finalOverhead = overhead.map(row => [...row]);

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(finalGround) },
        { name: 'Objects', data: this.flattenGrid(finalObjects) },
        { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        generator: { type: 'string', value: 'suburban_home' },
        rooms: { type: 'string', value: JSON.stringify(rooms) },
      },
    });
  }

  // ---- BSP helpers ----

  _bspSplit(node, depth, minW, minH) {
    if (depth <= 0) return;
    if (node.w < minW * 2 + 1 && node.h < minH * 2 + 1) return;

    let splitH;
    if (node.w < minW * 2 + 1) splitH = true;
    else if (node.h < minH * 2 + 1) splitH = false;
    else splitH = node.h > node.w ? this.rng.next() < 0.7 : this.rng.next() < 0.3;

    if (splitH) {
      const minSplit = minH;
      const maxSplit = node.h - minH;
      if (maxSplit <= minSplit) return;
      const split = this.rng.nextInt(minSplit, maxSplit);
      node.left = { x: node.x, y: node.y, w: node.w, h: split };
      node.right = { x: node.x, y: node.y + split, w: node.w, h: node.h - split };
    } else {
      const minSplit = minW;
      const maxSplit = node.w - minW;
      if (maxSplit <= minSplit) return;
      const split = this.rng.nextInt(minSplit, maxSplit);
      node.left = { x: node.x, y: node.y, w: split, h: node.h };
      node.right = { x: node.x + split, y: node.y, w: node.w - split, h: node.h };
    }

    this._bspSplit(node.left, depth - 1, minW, minH);
    this._bspSplit(node.right, depth - 1, minW, minH);
  }

  _bspLeaves(node) {
    if (!node.left && !node.right) return [node];
    const result = [];
    if (node.left) result.push(...this._bspLeaves(node.left));
    if (node.right) result.push(...this._bspLeaves(node.right));
    return result;
  }

  _assignRoomTypes(leaves, houseLeft, houseTop, houseW, houseH, types) {
    const sorted = [...leaves].sort((a, b) => {
      const ay = (a.y - houseTop) / houseH;
      const by = (b.y - houseTop) / houseH;
      const ax = (a.x - houseLeft) / houseW;
      const bx = (b.x - houseLeft) / houseW;
      return (ay + ax) - (by + bx);
    });
    for (let i = 0; i < sorted.length; i++) {
      sorted[i].roomType = types[i % types.length];
    }
  }

  _getRoomTypesForBedrooms(bedrooms) {
    switch (bedrooms) {
      case 2: return ['master', 'bedroom', 'bathroom', 'living'];
      case 3: return ['master', 'bedroom', 'bedroom', 'bathroom', 'kitchen', 'living'];
      case 4: return ['master', 'bedroom', 'bedroom', 'bedroom', 'bathroom', 'bathroom', 'kitchen', 'living', 'dining'];
      case 5: return ['master', 'bedroom', 'bedroom', 'bedroom', 'bedroom', 'bathroom', 'bathroom', 'restroom', 'kitchen', 'living', 'dining', 'office'];
      default: return ['master', 'bedroom', 'bathroom', 'living'];
    }
  }

  _roomTypeToZone(roomType) {
    const map = {
      master: ZONE.MASTER, bedroom: ZONE.BEDROOM, bathroom: ZONE.BATHROOM,
      kitchen: ZONE.KITCHEN, living: ZONE.LIVING, dining: ZONE.DINING,
      office: ZONE.OFFICE, storage: ZONE.STORAGE, hallway: ZONE.HALLWAY,
      laundry: ZONE.LAUNDRY, store: ZONE.STORE, backroom: ZONE.BACKROOM,
      restroom: ZONE.RESTROOM,
    };
    return map[roomType] || ZONE.FLOOR;
  }

  // ---- Interior walls ----

  _placeBSPWalls(ground, objects, node) {
    if (!node.left || !node.right) return;

    const isHSplit = node.left.y !== node.right.y && node.left.x === node.right.x;

    if (isHSplit) {
      const wallRow = node.left.y + node.left.h;
      if (wallRow >= this.height) return;
      for (let c = node.x; c < node.x + node.w; c++) {
        this.setCell(objects, c, wallRow, ZONE.WALL_INT);
      }
      const doorCol = Math.floor(node.x + node.w / 2);
      if (doorCol > node.x && doorCol < node.x + node.w - 1) {
        this.setCell(objects, doorCol, wallRow, ZONE.DOORFRAME);
        if (doorCol + 1 < node.x + node.w - 1) {
          this.setCell(objects, doorCol + 1, wallRow, ZONE.DOORFRAME);
        }
      }
    } else {
      const wallCol = node.left.x + node.left.w;
      if (wallCol >= this.width) return;
      for (let r = node.y; r < node.y + node.h; r++) {
        this.setCell(objects, wallCol, r, ZONE.WALL_INT);
      }
      const doorRow = Math.floor(node.y + node.h / 2);
      if (doorRow > node.y && doorRow < node.y + node.h - 1) {
        this.setCell(objects, wallCol, doorRow, ZONE.DOORFRAME);
        if (doorRow + 1 < node.y + node.h - 1) {
          this.setCell(objects, wallCol, doorRow + 1, ZONE.DOORFRAME);
        }
      }
    }

    this._placeBSPWalls(ground, objects, node.left);
    this._placeBSPWalls(ground, objects, node.right);
  }

  // ---- Outdoor features ----

  _fillGrassZone(ground) {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        ground[y][x] = ZONE.GRASS;
      }
    }
  }

  _fillRoadZone(ground, roadTop, roadHeight) {
    for (let r = roadTop; r < roadTop + roadHeight && r < this.height; r++) {
      for (let c = 0; c < this.width; c++) {
        this.setCell(ground, c, r, ZONE.ROAD);
      }
    }
  }

  _fillSidewalkRow(ground, row) {
    if (row < 0 || row >= this.height) return;
    for (let c = 0; c < this.width; c++) {
      this.setCell(ground, c, row, ZONE.SIDEWALK);
    }
  }

  _placePerimeterWalls(objects, hl, ht, hr, hb) {
    for (let c = hl; c < hr; c++) {
      this.setCell(objects, c, ht, ZONE.WALL_EXT);
      this.setCell(objects, c, hb - 1, ZONE.WALL_EXT);
    }
    for (let r = ht; r < hb; r++) {
      this.setCell(objects, hl, r, ZONE.WALL_EXT);
      this.setCell(objects, hr - 1, r, ZONE.WALL_EXT);
    }
  }

  _placeExteriorWindows(objects, hl, ht, hr, hb, interval) {
    // Check if a position is at or adjacent to a door (within 1 tile along the wall)
    const _nearDoor = (col, row) => {
      for (let dc = -1; dc <= 1; dc++) {
        const cell = this.getCell(objects, col + dc, row);
        if (cell === ZONE.DOOR || cell === ZONE.DOORFRAME) return true;
      }
      for (let dr = -1; dr <= 1; dr++) {
        const cell = this.getCell(objects, col, row + dr);
        if (cell === ZONE.DOOR || cell === ZONE.DOORFRAME) return true;
      }
      return false;
    };

    // Horizontal walls (top and bottom) — skip corners (first/last tile)
    for (let c = hl + 2; c < hr - 2; c++) {
      if ((c - hl) % interval === 0) {
        if (this.getCell(objects, c, ht) === ZONE.WALL_EXT && !_nearDoor(c, ht)) {
          this.setCell(objects, c, ht, ZONE.WINDOW);
        }
        if (this.getCell(objects, c, hb - 1) === ZONE.WALL_EXT && !_nearDoor(c, hb - 1)) {
          this.setCell(objects, c, hb - 1, ZONE.WINDOW);
        }
      }
    }
    // Vertical walls (left and right) — skip corners (first/last tile)
    for (let r = ht + 2; r < hb - 2; r++) {
      if ((r - ht) % interval === 0) {
        if (this.getCell(objects, hl, r) === ZONE.WALL_EXT && !_nearDoor(hl, r)) {
          this.setCell(objects, hl, r, ZONE.WINDOW);
        }
        if (this.getCell(objects, hr - 1, r) === ZONE.WALL_EXT && !_nearDoor(hr - 1, r)) {
          this.setCell(objects, hr - 1, r, ZONE.WINDOW);
        }
      }
    }
  }

  _placeWalkway(ground, col, fromRow, toRow, width) {
    const startRow = Math.min(fromRow, toRow);
    const endRow = Math.max(fromRow, toRow);
    for (let r = startRow; r <= endRow; r++) {
      for (let dc = 0; dc < width; dc++) {
        this.setCell(ground, col + dc, r, ZONE.SIDEWALK);
      }
    }
  }

  _placeDriveway(ground, col, fromRow, toRow, width) {
    const startRow = Math.min(fromRow, toRow);
    const endRow = Math.max(fromRow, toRow);
    for (let r = startRow; r <= endRow; r++) {
      for (let dc = 0; dc < width; dc++) {
        this.setCell(ground, col + dc, r, ZONE.DRIVEWAY);
      }
    }
  }

  _placeBackyardFence(objects, hl, ht, hr, fenceDepth) {
    const fenceTop = ht - fenceDepth;
    for (let c = hl - 3; c <= hr + 2; c++) {
      this.setCell(objects, c, fenceTop, ZONE.FENCE);
    }
    for (let r = fenceTop; r <= ht; r++) {
      this.setCell(objects, hl - 3, r, ZONE.FENCE);
      this.setCell(objects, hr + 2, r, ZONE.FENCE);
    }
  }

  _scatterYardDecorations(objects, hl, ht, hr, hb, treeCount, bushCount) {
    const margin = 3;
    const placed = new Set();

    const tryPlace = (zone, count) => {
      let n = 0;
      for (let attempt = 0; attempt < count * 10 && n < count; attempt++) {
        const c = this.rng.nextInt(margin, this.width - margin - 1);
        const r = this.rng.nextInt(margin, this.height - margin - 1);
        if (c >= hl - 1 && c < hr + 1 && r >= ht - 1 && r < hb + 1) continue;
        const key = `${c},${r}`;
        if (placed.has(key)) continue;
        placed.add(key);
        this.setCell(objects, c, r, zone);
        n++;
      }
    };

    tryPlace(ZONE.TREE, treeCount);
    tryPlace(ZONE.BUSH, bushCount);
  }

  _placeRoadCars(objects, roadTop, hl, hr, count) {
    for (let i = 0; i < count; i++) {
      const c = this.rng.nextInt(4, this.width - 4);
      if (c >= hl - 1 && c <= hr + 1) continue;
      this.setCell(objects, c, roadTop + 1, ZONE.CRATE);
    }
  }

  // ---- Zone resolution ----

  _buildTileMapping(tiles) {
    const mapping = {};
    mapping[ZONE.WALL] = { center: tiles.wall };
    mapping[ZONE.WALL_EXT] = { center: tiles.wallExt || tiles.wall };
    mapping[ZONE.WALL_INT] = { center: tiles.wallInt || tiles.wall };
    mapping[ZONE.FLOOR] = { center: tiles.floor };
    mapping[ZONE.DOOR] = { center: tiles.door || tiles.floor };
    mapping[ZONE.DOORFRAME] = { center: tiles.doorframe || tiles.floor };
    mapping[ZONE.GRASS] = { center: tiles.grass || 1 };
    mapping[ZONE.ROAD] = { center: tiles.road || tiles.asphalt || 1 };
    mapping[ZONE.SIDEWALK] = { center: tiles.sidewalk || 1 };
    mapping[ZONE.DRIVEWAY] = { center: tiles.driveway || tiles.sidewalk || 1 };
    mapping[ZONE.ASPHALT] = { center: tiles.asphalt || 1 };
    mapping[ZONE.MASTER] = { center: tiles.floorMaster || tiles.floor };
    mapping[ZONE.BEDROOM] = { center: tiles.floorBedroom || tiles.floor };
    mapping[ZONE.BATHROOM] = { center: tiles.floorBathroom || tiles.floor };
    mapping[ZONE.KITCHEN] = { center: tiles.floorKitchen || tiles.floor };
    mapping[ZONE.LIVING] = { center: tiles.floorLiving || tiles.floor };
    mapping[ZONE.DINING] = { center: tiles.floorDining || tiles.floor };
    mapping[ZONE.OFFICE] = { center: tiles.floorOffice || tiles.floor };
    return mapping;
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.EMPTY]: 0,
      [ZONE.GRASS]: tiles.grass || 1,
      [ZONE.ROAD]: tiles.road || tiles.asphalt || 1,
      [ZONE.SIDEWALK]: tiles.sidewalk || 1,
      [ZONE.DRIVEWAY]: tiles.driveway || tiles.sidewalk || 1,
      [ZONE.ASPHALT]: tiles.asphalt || 1,
      [ZONE.WALL]: tiles.wall,
      [ZONE.FLOOR]: tiles.floor,
      [ZONE.DOOR]: tiles.door || tiles.floor,
      [ZONE.MASTER]: tiles.floorMaster || tiles.floor,
      [ZONE.BEDROOM]: tiles.floorBedroom || tiles.floor,
      [ZONE.BATHROOM]: tiles.floorBathroom || tiles.floor,
      [ZONE.KITCHEN]: tiles.floorKitchen || tiles.floor,
      [ZONE.LIVING]: tiles.floorLiving || tiles.floor,
      [ZONE.DINING]: tiles.floorDining || tiles.floor,
      [ZONE.OFFICE]: tiles.floorOffice || tiles.floor,
      [ZONE.RESTROOM]: tiles.floorBathroom || tiles.floor,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  _resolveObjectZones(grid, tiles) {
    const mapping = {
      [ZONE.EMPTY]: 0,
      [ZONE.WALL_EXT]: tiles.wallExt || tiles.wall,
      [ZONE.WALL_INT]: tiles.wallInt || tiles.wall,
      [ZONE.WINDOW]: tiles.window || 0,
      [ZONE.DOOR]: tiles.door || tiles.floor,
      [ZONE.DOORFRAME]: tiles.doorframe || tiles.floor,
      [ZONE.FENCE]: tiles.fence || 0,
      [ZONE.TREE]: tiles.tree || 0,
      [ZONE.BUSH]: tiles.bush || 0,
      [ZONE.CRATE]: tiles.crate || 0,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  static DEFAULT_TILES = {
    wall: 5,
    wallExt: 5,
    wallInt: 5,
    floor: 6,
    door: 7,
    doorframe: 6,
    grass: 1,
    road: 16,
    asphalt: 16,
    sidewalk: 8,
    driveway: 8,
    window: 5,
    fence: 23,
    tree: 10,
    bush: 11,
    crate: 41,
    floorMaster: 6,
    floorBedroom: 6,
    floorBathroom: 6,
    floorKitchen: 6,
    floorLiving: 25,
    floorDining: 6,
    floorOffice: 6,
  };

  static DEFAULT_OPTIONS = {
    bedrooms: 3,
    quality: 'draft',
  };

  static getEmittedZones() {
    return [
      ZONE.GRASS, ZONE.ROAD, ZONE.SIDEWALK, ZONE.DRIVEWAY, ZONE.ASPHALT,
      ZONE.WALL_EXT, ZONE.WALL_INT, ZONE.DOOR, ZONE.DOORFRAME, ZONE.WINDOW, ZONE.FENCE,
      ZONE.FLOOR, ZONE.MASTER, ZONE.BEDROOM, ZONE.BATHROOM, ZONE.KITCHEN,
      ZONE.LIVING, ZONE.DINING, ZONE.OFFICE, ZONE.STORAGE, ZONE.HALLWAY,
      ZONE.LAUNDRY, ZONE.STORE, ZONE.BACKROOM, ZONE.RESTROOM,
      ZONE.TREE, ZONE.BUSH, ZONE.CRATE,
    ];
  }
}
